import React, { useState } from 'react';
import { Shuffle, AlertTriangle, List, ChevronDown, ChevronUp } from 'lucide-react';
import { useClass } from '../context/ClassContext';
import { scenarios } from '../data/scenarios';
import type { NiveauFilter, ScenarioAssignment as ScenarioAssignmentType } from '../types';

export default function ScenarioAssignmentComponent() {
  const { currentClass, assignScenarios, clearAssignments } = useClass();
  const [selectedLevels, setSelectedLevels] = useState<NiveauFilter[]>([]);
  const [showAllScenarios, setShowAllScenarios] = useState(false);

  const handleLevelToggle = (level: NiveauFilter) => {
    setSelectedLevels(prev => 
      prev.includes(level)
        ? prev.filter(l => l !== level)
        : [...prev, level]
    );
  };

  // Fonction Fisher-Yates (Knuth) shuffle pour un mélange vraiment aléatoire
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const assignRandomScenarios = () => {
    if (!currentClass || !currentClass.students.length) return;

    // Filtrer les scénarios selon les niveaux sélectionnés
    const availableScenarios = scenarios.filter(scenario =>
      selectedLevels.includes(scenario.niveau as NiveauFilter)
    );

    if (!availableScenarios.length) {
      alert('Veuillez sélectionner au moins un niveau de scénario');
      return;
    }

    // Mélanger les étudiants et les scénarios
    const shuffledStudents = shuffleArray(currentClass.students);
    const shuffledScenarios = shuffleArray(availableScenarios);

    // Créer les assignations en utilisant les tableaux mélangés
    const assignments: ScenarioAssignmentType[] = shuffledStudents.map((student, index) => ({
      studentId: student.id,
      scenarioId: shuffledScenarios[index % shuffledScenarios.length].id
    }));

    // Mélanger une dernière fois les assignations pour plus d'aléatoire
    const finalAssignments = shuffleArray(assignments);

    assignScenarios(finalAssignments);
  };

  const getNiveauColor = (niveau: string) => {
    switch (niveau) {
      case 'Débutant':
        return 'bg-green-100 text-green-800';
      case 'Intermédiaire':
        return 'bg-yellow-100 text-yellow-800';
      case 'Avancé':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Médical':
        return 'bg-blue-100 text-blue-800';
      case 'Traumatique':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!currentClass || !currentClass.students.length) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
        <div className="flex items-center">
          <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2" />
          <p className="text-sm text-yellow-700">
            Veuillez d'abord créer une classe et ajouter des élèves.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">
        Affectation aléatoire des scénarios
      </h2>

      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-2">
          Sélectionnez les niveaux de scénarios à inclure :
        </p>
        <div className="flex flex-wrap gap-2">
          {(['Débutant', 'Intermédiaire', 'Avancé'] as NiveauFilter[]).map((level) => (
            <button
              key={level}
              onClick={() => handleLevelToggle(level)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors
                ${selectedLevels.includes(level)
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {level}
            </button>
          ))}
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={assignRandomScenarios}
          disabled={selectedLevels.length === 0}
          className={`flex items-center px-4 py-2 rounded-md text-white
            ${selectedLevels.length === 0
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
            }`}
        >
          <Shuffle className="h-5 w-5 mr-2" />
          Assigner les scénarios
        </button>
        <button
          onClick={clearAssignments}
          className="flex items-center px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Réinitialiser les assignations
        </button>
      </div>

      {/* Liste des assignations */}
      {currentClass.students.some(s => s.assignedScenario) && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Assignations actuelles
          </h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <ul className="space-y-2">
              {currentClass.students.map((student) => {
                const assignedScenario = scenarios.find(
                  s => s.id === student.assignedScenario
                );
                return (
                  <li key={student.id} className="flex items-center justify-between">
                    <span className="text-gray-900">
                      {student.firstName} {student.lastName}
                    </span>
                    {assignedScenario && (
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(assignedScenario.type)}`}>
                          {assignedScenario.type}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getNiveauColor(assignedScenario.niveau)}`}>
                          {assignedScenario.niveau}
                        </span>
                        <span className="text-sm text-gray-600">
                          {assignedScenario.titre}
                        </span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Bouton pour afficher tous les scénarios */}
      <div className="mt-8 border-t pt-6">
        <button
          onClick={() => setShowAllScenarios(!showAllScenarios)}
          className="flex items-center justify-between w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <div className="flex items-center">
            <List className="h-5 w-5 mr-2 text-gray-600" />
            <span className="font-medium text-gray-900">
              {showAllScenarios ? 'Masquer' : 'Montrer'} la liste complète des scénarios
            </span>
          </div>
          {showAllScenarios ? (
            <ChevronUp className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-600" />
          )}
        </button>

        {/* Liste de tous les scénarios */}
        {showAllScenarios && (
          <div className="mt-4 space-y-4">
            {scenarios.map((scenario) => (
              <div key={scenario.id} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{scenario.titre}</h4>
                  <div className="flex space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(scenario.type)}`}>
                      {scenario.type}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getNiveauColor(scenario.niveau)}`}>
                      {scenario.niveau}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600">{scenario.description}</p>
                <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                  <span>Durée: {scenario.duree} min</span>
                  <span>Participants: {scenario.participants}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}